import {useState} from 'react';
import {Card, CardContent, CardHeader, CardTitle} from '@common-web/components/ui/card';
import {Button} from '@common-web/components/ui/button';
import {Badge} from '@common-web/components/ui/badge';
import {Alert, AlertDescription} from '@common-web/components/ui/alert';
import {DataTable, type Column} from '@common-web/components/data-table';
import {trpc} from '@common-web/lib/trpc';
import {FileText, Download, Trash2, Calendar, User, AlertCircle} from 'lucide-react';
import {RfcAttachmentType} from '@common/types/rfc';
import {toast} from '@common-web/hooks/use-toast';
import {reportError} from '@common-web/lib/errorReporting';

interface FileAttachment {
  id: string;
  fileName: string;
  fileType: RfcAttachmentType;
  fileSize: number;
  contentType?: string | null;
  s3Key: string;
  s3Bucket: string;
  uploadedBy?: string | null;
  uploadedByUser?: {
    id: string;
    name: string;
    email: string;
  } | null;
  createdAt: string;
}

export function RfcFileList({
  attachments,
  showDownloadLinks = false,
  showAdminActions = false,
  rfcAccessToken,
  rfcPassword,
  refreshList,
}: {
  attachments: FileAttachment[];
  showDownloadLinks?: boolean;
  showAdminActions?: boolean;
  rfcAccessToken?: string;
  rfcPassword?: string;
  refreshList: () => void;
}) {
  const [downloadingFiles, setDownloadingFiles] = useState<Set<string>>(new Set());
  const [deletingFiles, setDeletingFiles] = useState<Set<string>>(new Set());

  const generateDownloadUrlMutation = trpc.clients.rfc.adminGenerateDownloadUrl.useMutation();
  const publicDownloadUrlMutation = trpc.clients.rfc.generateDownloadUrl.useMutation();
  const deleteAttachmentMutation = trpc.clients.rfc.deleteAttachment.useMutation();

  const getFileTypeLabel = (fileType: RfcAttachmentType) => {
    switch (fileType) {
      case RfcAttachmentType.CENSUS:
        return 'Employee Census';
      case RfcAttachmentType.LOCATION_DETAILS:
        return 'Location Details';
      case RfcAttachmentType.BANK_LETTER:
        return 'Bank Letter';
      case RfcAttachmentType.VOIDED_CHECK:
        return 'Voided Check';
      case RfcAttachmentType.SIGNED_ACH:
        return 'Signed ACH Form';
      case RfcAttachmentType.OTHER:
        return 'Other Document';
      default:
        return fileType;
    }
  };

  const getFileTypeColor = (fileType: RfcAttachmentType) => {
    switch (fileType) {
      case RfcAttachmentType.CENSUS:
        return 'bg-blue-100 text-blue-800';
      case RfcAttachmentType.LOCATION_DETAILS:
        return 'bg-green-100 text-green-800';
      case RfcAttachmentType.BANK_LETTER:
        return 'bg-purple-100 text-purple-800';
      case RfcAttachmentType.VOIDED_CHECK:
        return 'bg-orange-100 text-orange-800';
      case RfcAttachmentType.SIGNED_ACH:
        return 'bg-red-100 text-red-800';
      case RfcAttachmentType.OTHER:
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDownload = async (attachment: FileAttachment) => {
    if (downloadingFiles.has(attachment.id)) return;

    setDownloadingFiles((prev) => new Set(prev).add(attachment.id));
    try {
      let downloadUrl: string;

      if (rfcAccessToken && rfcPassword) {
        // Public download via RFC access token (unauthenticated flow)
        const result = await publicDownloadUrlMutation.mutateAsync({
          accessToken: rfcAccessToken,
          password: rfcPassword,
          attachmentId: attachment.id,
        });
        downloadUrl = result.downloadUrl;
      } else {
        // Authenticated download via attachment ID
        const result = await generateDownloadUrlMutation.mutateAsync({
          attachmentId: attachment.id,
        });
        downloadUrl = result.downloadUrl;
      }

      window.open(downloadUrl, '_blank');
    } catch (error) {
      console.error('Download failed:', error);
      toast({
        title: 'Download failed:',
        description: error instanceof Error ? error.message : 'An unknown error occurred',
        variant: 'destructive',
      });
    } finally {
      setDownloadingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  const handleDelete = async (attachment: FileAttachment) => {
    if (!rfcAccessToken || !rfcPassword || deletingFiles.has(attachment.id)) return;

    if (!confirm(`Are you sure you want to delete "${attachment.fileName}"?`)) return;

    setDeletingFiles((prev) => new Set(prev).add(attachment.id));

    try {
      await deleteAttachmentMutation.mutateAsync({
        accessToken: rfcAccessToken,
        password: rfcPassword,
        attachmentId: attachment.id,
      });
      refreshList();
    } catch (error) {
      reportError(error, {title: 'Delete failed', context: 'RfcFileList.delete'});
    } finally {
      setDeletingFiles((prev) => {
        const newSet = new Set(prev);
        newSet.delete(attachment.id);
        return newSet;
      });
    }
  };

  const showActions = showDownloadLinks || showAdminActions;

  const columns: Column<FileAttachment>[] = [
    {
      id: 'file',
      header: 'File',
      render: (attachment) => (
        <div className='flex items-center gap-3'>
          <FileText className='h-4 w-4 text-blue-500' />
          <div>
            <div className='font-medium text-sm'>{attachment.fileName}</div>
            {attachment.contentType && (
              <div className='text-xs text-muted-foreground'>{attachment.contentType}</div>
            )}
          </div>
        </div>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      render: (attachment) => (
        <Badge variant='outline' className={`text-xs ${getFileTypeColor(attachment.fileType)}`}>
          {getFileTypeLabel(attachment.fileType)}
        </Badge>
      ),
    },
    {
      id: 'size',
      header: 'Size',
      render: (attachment) => (
        <span className='text-sm'>{formatFileSize(attachment.fileSize)}</span>
      ),
    },
    {
      id: 'uploaded',
      header: 'Uploaded',
      render: (attachment) => (
        <div className='space-y-1'>
          <div className='flex items-center gap-2 text-sm'>
            <Calendar className='h-3 w-3' />
            {new Date(attachment.createdAt).toLocaleDateString()}
          </div>
          {attachment.uploadedByUser ? (
            <div className='flex items-center gap-2 text-xs text-muted-foreground'>
              <User className='h-3 w-3' />
              {attachment.uploadedByUser.name}
            </div>
          ) : null}
        </div>
      ),
    },
  ];

  if (showActions) {
    columns.push({
      id: 'actions',
      header: 'Actions',
      render: (attachment) => (
        <div className='flex items-center gap-2'>
          {showDownloadLinks && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => handleDownload(attachment)}
              disabled={downloadingFiles.has(attachment.id)}
            >
              {downloadingFiles.has(attachment.id) ? (
                <div className='h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-blue-600' />
              ) : (
                <Download className='h-4 w-4' />
              )}
            </Button>
          )}

          {showAdminActions && rfcAccessToken && rfcPassword && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => handleDelete(attachment)}
              disabled={deletingFiles.has(attachment.id)}
              className='text-red-600 hover:text-red-700 hover:bg-red-50'
            >
              {deletingFiles.has(attachment.id) ? (
                <div className='h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-red-600' />
              ) : (
                <Trash2 className='h-4 w-4' />
              )}
            </Button>
          )}
        </div>
      ),
    });
  }

  if (attachments.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className='flex items-center gap-2'>
            <FileText className='h-5 w-5' />
            Attachments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className='text-center py-8'>
            <FileText className='h-12 w-12 text-muted-foreground mx-auto mb-4' />
            <p className='text-muted-foreground'>No files attached</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className='flex items-center gap-2'>
          <FileText className='h-5 w-5' />
          Attachments ({attachments.length})
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className='space-y-4'>
          <DataTable
            columns={columns}
            data={attachments}
            rowKey={(attachment) => attachment.id}
            emptyMessage='No files attached'
          />
          {generateDownloadUrlMutation.error && (
            <Alert>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>
                Failed to download file: {generateDownloadUrlMutation.error.message}
              </AlertDescription>
            </Alert>
          )}
          {publicDownloadUrlMutation.error && (
            <Alert>
              <AlertCircle className='h-4 w-4' />
              <AlertDescription>
                Failed to download file: {publicDownloadUrlMutation.error.message}
              </AlertDescription>
            </Alert>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
